import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { MCTDetailData } from 'api/traps/types';
import DeviceLinkCell from 'components/TrapsTables/Cells/LinkCell/DevicesLinkCall';
import CustomPlaceholder from 'components/Utils/CustomPlaceHolder';
import './TrapDataTableCard.scss';
import { SnapTrapStatus } from 'api/traps/STTraps/types';
import { TrapTableType, TrapTableTypeLink } from 'components/TrapsTables/trapTables';

interface TrapDataTableCardProps {
  data: MCTDetailData | null;
  isLoading: boolean;
  isRefetching: boolean;
  slug: string;
  type: TrapTableType;
  deviceType: TrapTableTypeLink;
}

const TrapDataTableCard: React.FC<TrapDataTableCardProps> = ({ data, isLoading, isRefetching, type, deviceType }) => {
  const { t } = useTranslation();

  const pestStatus = (() => {
    switch (true) {
      case data?.pest_amount && data.pest_amount >= 1:
        return 'danger';
      default:
        return null;
    }
  })();

  const getSnapTrapStatus = (status: SnapTrapStatus) => {
    switch (status) {
      case SnapTrapStatus.SET:
        return t('STDetail.TrapDataTableCard.statusSet');
      case SnapTrapStatus.DARK:
        return t('STDetail.TrapDataTableCard.statusDark');
      case SnapTrapStatus.TRIPPED_EMPTY:
        return t('STDetail.TrapDataTableCard.statusTrippedEmpty');
      case SnapTrapStatus.TRIPPED_CAUGHT:
        return t('STDetail.TrapDataTableCard.statusTrippedCaught');
      case SnapTrapStatus.NO_SET:
        return t('STDetail.TrapDataTableCard.statusNoSet');
    }
  };

  const getSnapTrapStatusColor = (status: SnapTrapStatus) => {
    switch (status) {
      case SnapTrapStatus.SET:
        return 'text-success';
      case SnapTrapStatus.DARK:
      case SnapTrapStatus.NO_SET:
        return 'text-secondary';
      case SnapTrapStatus.TRIPPED_EMPTY:
        return 'text-warning';
      case SnapTrapStatus.TRIPPED_CAUGHT:
        return 'text-danger';
    }
  };

  const link = `/r/object/interactive-map/${data?.object.slug}?device_zoom=${data?.slug}`;
  const shouldShowLastService = () => {
    if (!data?.last_maintenance || !data?.date_of_installation) return false;

    const lastMaintenanceDate = new Date(data.last_maintenance).getTime();
    const installationDate = new Date(data.date_of_installation).getTime();

    return lastMaintenanceDate > installationDate;
  };
  return (
    <div className="card shadow-lg">
      <div className="card-body text-center">
        <div className="card-body">
          <div className="table-card">
            <table className="table mb-0 table-sm">
              <tbody>
                <tr>
                  <td className="fw-medium align-content-center text-start">
                    {t('MCTDetail.TrapDataTableCard.last_maintance')}
                  </td>
                  <td className="align-content-center text-center ">
                    {isLoading || isRefetching ? (
                      <CustomPlaceholder colSize={12} />
                    ) : shouldShowLastService() ? (
                      dayjs(data?.last_maintenance).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">
                    {t('MCTDetail.TrapDataTableCard.model')}
                  </td>
                  <td>{isLoading || isRefetching ? <CustomPlaceholder colSize={12} /> : `${data?.model}`}</td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">
                    {t('MCTDetail.TrapDataTableCard.serialNumber')}
                  </td>
                  <td className="w-50">
                    {isLoading || isRefetching ? <CustomPlaceholder colSize={10} /> : `${data?.serial_number || 'N/A'}`}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">{t('MCTDetail.TrapDataTableCard.no')}</td>
                  <td>{isLoading || isRefetching ? <CustomPlaceholder colSize={2} /> : `${data?.sequence_number}`}</td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">
                    {t('MCTDetail.TrapDataTableCard.installDate')}
                  </td>
                  <td>
                    {isLoading || isRefetching ? (
                      <CustomPlaceholder colSize={7} />
                    ) : data?.date_of_installation ? (
                      dayjs(data.date_of_installation).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start ">
                    {t('MCTDetail.TrapDataTableCard.lastConnection')}
                  </td>
                  <td>
                    {isLoading || isRefetching ? (
                      <CustomPlaceholder colSize={12} />
                    ) : data?.last_connect ? (
                      data?.last_connect ? (
                        dayjs(data?.last_connect).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm')
                      ) : (
                        '-'
                      )
                    ) : (
                      '---'
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">{t('MCTDetail.TrapDataTableCard.hub')}</td>
                  <td>
                    {isLoading || isRefetching ? (
                      <CustomPlaceholder colSize={9} />
                    ) : (
                      <Link className="text-info" to={`/hub/info/${data?.hub.toLowerCase()}/`}>
                        {data?.hub}
                      </Link>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="fw-medium align-content-center text-start">
                    {t('MCTDetail.TrapDataTableCard.battery')}
                  </td>
                  <td>
                    {isLoading || isRefetching ? <CustomPlaceholder colSize={3} /> : `${data?.battery_percentage}`}
                  </td>
                </tr>
                {type !== TrapTableType.MultiCatchMouseTraps ? (
                  <tr>
                    <td
                      className="fw-medium align-content-center text-start"
                      title={t('STDetail.TrapDataTableCard.connectedTrapsTooltip')}
                    >
                      {t('STDetail.TrapDataTableCard.connectedTraps')}
                    </td>
                    <td>
                      {isLoading || isRefetching ? (
                        <CustomPlaceholder colSize={2} />
                      ) : (
                        <a href={link} className="alert my-0 py-0 rounded-pill">
                          {data?.snaps_count ? <strong> {data?.snaps_count} </strong> : 0}
                        </a>
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr className={pestStatus === 'danger' ? 'mice-border-danger' : 'mice-border-clear'}>
                    <td
                      className={`fw-medium align-content-center text-start ${pestStatus === 'danger' ? 'text-danger' : ''}`}
                    >
                      {t('MCTDetail.TrapDataTableCard.miceInTrap')}
                    </td>
                    <td>
                      {isLoading || isRefetching ? (
                        <CustomPlaceholder colSize={2} />
                      ) : (
                        <a href={link} className={`alert alert-${pestStatus} my-0 py-0 rounded-pill`} role="alert">
                          {data?.pest_amount ? <strong> {data?.pest_amount} </strong> : 0}
                        </a>
                      )}
                    </td>
                  </tr>
                )}
                {data?.snap_1_status !== undefined && (
                  <tr>
                    <td className="fw-medium align-content-center text-start">
                      {type === TrapTableType.SnapTrapRat
                        ? t('STDetail.TrapDataTableCard.snapTrapRat')
                        : t('STDetail.TrapDataTableCard.snapTrap1')}
                    </td>
                    <td>
                      {isLoading || isRefetching ? (
                        <CustomPlaceholder colSize={2} />
                      ) : (
                        <a
                          title={
                            data?.snap_1_status === SnapTrapStatus.SET ? t('STDetail.TrapDataTableCard.snapHint') : ''
                          }
                          href={link}
                          className="alert my-0 py-0 rounded-pill d-inline-block"
                        >
                          {data?.snap_1_status ? (
                            <strong className={`${getSnapTrapStatusColor(data?.snap_1_status)} text-center d-block`}>
                              {getSnapTrapStatus(data?.snap_1_status)
                                ?.split('-')
                                .map((part: string, index: number, arr: string[]) => (
                                  <span key={index} className="d-block m-0 p-0" style={{ lineHeight: '1.1' }}>
                                    {part.trim()}
                                    {index < arr.length - 1 ? ' -' : ''}
                                  </span>
                                ))}
                            </strong>
                          ) : (
                            'N/A'
                          )}
                        </a>
                      )}
                    </td>
                  </tr>
                )}
                {data?.snap_2_status !== undefined && type === TrapTableType.SnapTrapMouse && (
                  <tr>
                    <td className="fw-medium align-content-center text-start">
                      {t('STDetail.TrapDataTableCard.snapTrap2')}
                    </td>
                    <td>
                      {isLoading || isRefetching ? (
                        <CustomPlaceholder colSize={2} />
                      ) : (
                        <a
                          title={
                            data?.snap_2_status === SnapTrapStatus.SET ? t('STDetail.TrapDataTableCard.snapHint') : ''
                          }
                          href={link}
                          className="alert my-0 py-0 rounded-pill d-inline-block"
                        >
                          {data?.snap_2_status ? (
                            <strong
                              className={`${getSnapTrapStatusColor(data?.snap_2_status)} d-flex flex-column align-items-center justify-content-center text-center mb-0 m-0`}
                            >
                              {getSnapTrapStatus(data?.snap_2_status)
                                ?.split('-')
                                .map((part: string, index: number, arr: string[]) => (
                                  <span key={index} className="d-block m-0 p-0" style={{ lineHeight: '1.1' }}>
                                    {part.trim()}
                                    {index < arr.length - 1 ? ' -' : ''}
                                  </span>
                                ))}
                            </strong>
                          ) : (
                            'N/A'
                          )}
                        </a>
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="fw-medium align-content-center text-start">{t('MCTDetail.TrapDataTableCard.map')}</td>
                  <td>
                    {isLoading || isRefetching ? (
                      <CustomPlaceholder colSize={12} />
                    ) : (
                      <DeviceLinkCell
                        data={data?.default_plan ?? null}
                        icon="ri-treasure-map-line"
                        slug={data?.object.slug}
                        device_add_slug={data?.slug}
                        deviceType={deviceType || ''}
                      />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrapDataTableCard;
